/**
 * CodePanel — editovatelný Code mode pro Codable workspace.
 * Monaco theme = ACM barevné schema (CSS vars / data-theme).
 * Download souboru + upload nového do DEV workspace.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Editor, { type Monaco, type OnMount } from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  FileText,
  Folder,
  Loader2,
  Save,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { setUiContext } from '../../../lib/ui-context';
import { isAcmTheme, type AcmTheme } from '../../../lib/theme';
import { EmptyState, Input } from '../../../components/ui';

interface TreeEntry {
  path: string;
  name: string;
  type: 'blob' | 'tree';
}

interface TreeResp {
  ok: boolean;
  entries?: TreeEntry[];
  total?: number;
  error?: string;
}

interface ReadResp {
  ok: boolean;
  content?: string;
  size?: number;
  path?: string;
  encoding?: string;
  note?: string;
  error?: string;
}

interface TreeNode {
  name: string;
  path: string;
  type: 'blob' | 'tree';
  children?: TreeNode[];
}

const MONACO_THEME_ID = 'acm-ui';

const LIGHT_THEMES = new Set<AcmTheme>([
  'day',
  'slate',
  'primavera',
  'salon',
  'm365',
]);

/** RGB triplet z CSS var (`"3 7 18"`) → `#030712`. */
function cssRgbToHex(varName: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  const m = raw.match(/^(\d+)\s+(\d+)\s+(\d+)/);
  if (!m) return fallback;
  return (
    '#' +
    [m[1], m[2], m[3]]
      .map((n) => Number(n).toString(16).padStart(2, '0'))
      .join('')
  );
}

function hexAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${h}${a}`;
}

function currentAcmTheme(): AcmTheme {
  const attr = document.documentElement.getAttribute('data-theme');
  return isAcmTheme(attr) ? attr : 'night';
}

/** Monaco theme z aktuálních ACM CSS tokenů (shell / gray / plex). */
export function buildAcmMonacoTheme(): MonacoEditor.IStandaloneThemeData {
  const theme = currentAcmTheme();
  const light = LIGHT_THEMES.has(theme);

  const bg = cssRgbToHex('--gray-950', light ? '#f8fafc' : '#030712');
  const surface = cssRgbToHex('--gray-900', light ? '#f1f5f9' : '#111827');
  const surface2 = cssRgbToHex('--gray-800', light ? '#e2e8f0' : '#1f2937');
  const fg = cssRgbToHex('--gray-100', light ? '#1e293b' : '#f3f4f6');
  const muted = cssRgbToHex('--gray-500', light ? '#787e96' : '#6b7280');
  const dim = cssRgbToHex('--gray-600', light ? '#94a3b8' : '#4b5563');
  const plex = cssRgbToHex('--acm-plex', light ? '#2563eb' : '#3b82f6');
  const line = cssRgbToHex('--gray-400', light ? '#64748b' : '#9ca3af');

  // Token foregrounds: hex bez # (Monaco rules)
  const strip = (h: string) => h.replace(/^#/, '');
  const comment = strip(muted);
  const keyword = strip(plex);
  const string = light ? '047857' : '34d399'; // emerald
  const number = light ? 'b45309' : 'fbbf24';
  const type = light ? '7c3aed' : 'a78bfa';

  return {
    base: light ? 'vs' : 'vs-dark',
    inherit: true,
    rules: [
      { token: 'comment', foreground: comment, fontStyle: 'italic' },
      { token: 'keyword', foreground: keyword },
      { token: 'string', foreground: string },
      { token: 'number', foreground: number },
      { token: 'type', foreground: type },
      { token: 'delimiter', foreground: strip(line) },
    ],
    colors: {
      'editor.background': bg,
      'editor.foreground': fg,
      'editorLineNumber.foreground': dim,
      'editorLineNumber.activeForeground': muted,
      'editorCursor.foreground': plex,
      'editor.selectionBackground': hexAlpha(plex, light ? 0.22 : 0.28),
      'editor.inactiveSelectionBackground': hexAlpha(plex, 0.12),
      'editor.lineHighlightBackground': hexAlpha(surface, light ? 0.85 : 0.55),
      'editor.lineHighlightBorder': hexAlpha(plex, 0.0),
      'editorGutter.background': bg,
      'editorWidget.background': surface,
      'editorWidget.border': surface2,
      'editorSuggestWidget.background': surface,
      'editorSuggestWidget.border': surface2,
      'editorSuggestWidget.selectedBackground': hexAlpha(plex, 0.2),
      'editorHoverWidget.background': surface,
      'editorHoverWidget.border': surface2,
      'scrollbarSlider.background': hexAlpha(muted, 0.25),
      'scrollbarSlider.hoverBackground': hexAlpha(muted, 0.4),
      'scrollbarSlider.activeBackground': hexAlpha(plex, 0.45),
      'editorIndentGuide.background': hexAlpha(dim, 0.35),
      'editorIndentGuide.activeBackground': hexAlpha(muted, 0.55),
      'editorBracketMatch.background': hexAlpha(plex, 0.15),
      'editorBracketMatch.border': hexAlpha(plex, 0.5),
      'focusBorder': hexAlpha(plex, 0.4),
      'input.background': surface,
      'input.foreground': fg,
      'input.border': surface2,
    },
  };
}

function defineAndApplyAcmTheme(monaco: Monaco) {
  monaco.editor.defineTheme(MONACO_THEME_ID, buildAcmMonacoTheme());
  monaco.editor.setTheme(MONACO_THEME_ID);
}

function buildTree(entries: TreeEntry[]): TreeNode[] {
  const root: TreeNode[] = [];
  const dirMap = new Map<string, TreeNode>();

  const ensureDir = (path: string): TreeNode[] => {
    if (!path) return root;
    const existing = dirMap.get(path);
    if (existing) return existing.children!;
    const idx = path.lastIndexOf('/');
    const parentChildren = ensureDir(idx >= 0 ? path.slice(0, idx) : '');
    const node: TreeNode = { name: path.slice(idx + 1), path, type: 'tree', children: [] };
    dirMap.set(path, node);
    parentChildren.push(node);
    return node.children!;
  };

  for (const e of entries) {
    if (e.type === 'tree') {
      ensureDir(e.path);
      continue;
    }
    const idx = e.path.lastIndexOf('/');
    const parent = ensureDir(idx >= 0 ? e.path.slice(0, idx) : '');
    parent.push({ name: e.name || e.path.slice(idx + 1), path: e.path, type: 'blob' });
  }

  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) =>
      a.type === b.type ? a.name.localeCompare(b.name) : (a.type === 'tree' ? -1 : 1)
    );
    for (const n of nodes) if (n.children) sortRec(n.children);
  };
  sortRec(root);
  return root;
}

function formatSize(size: number): string {
  if (size < 1024) return `${size} B`;
  return `${(size / 1024).toFixed(1)} KB`;
}

export function langFromPath(path: string): string {
  const base = path.split('/').pop() ?? path;
  const ext = base.includes('.') ? base.split('.').pop()!.toLowerCase() : '';
  const map: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    mts: 'typescript',
    cts: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    mjs: 'javascript',
    cjs: 'javascript',
    json: 'json',
    css: 'css',
    scss: 'scss',
    less: 'less',
    html: 'html',
    htm: 'html',
    md: 'markdown',
    mdx: 'markdown',
    py: 'python',
    yml: 'yaml',
    yaml: 'yaml',
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    sql: 'sql',
    vue: 'html',
    svelte: 'html',
    toml: 'ini',
    env: 'ini',
    svg: 'xml',
    xml: 'xml',
    rs: 'rust',
    go: 'go',
    java: 'java',
    kt: 'kotlin',
    rb: 'ruby',
    php: 'php',
    c: 'c',
    h: 'c',
    cpp: 'cpp',
    hpp: 'cpp',
  };
  if (base === 'Dockerfile' || base.startsWith('Dockerfile.')) return 'dockerfile';
  return map[ext] ?? 'plaintext';
}

const TEXT_EXT = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'json', 'css', 'scss', 'less', 'html', 'htm',
  'md', 'mdx', 'py', 'yml', 'yaml', 'sh', 'bash', 'sql', 'vue', 'svelte', 'toml', 'env',
  'svg', 'xml', 'txt', 'csv', 'svg', 'gitignore', 'dockerignore', 'editorconfig',
  'prettierrc', 'eslintrc', 'mjs', 'cjs', 'map', 'lock', 'rs', 'go', 'java', 'kt',
  'rb', 'php', 'c', 'h', 'cpp', 'hpp', 'vue',
]);

function isProbablyTextFile(name: string, mime: string): boolean {
  if (mime.startsWith('text/')) return true;
  if (mime.includes('json') || mime.includes('xml') || mime.includes('javascript') ||
    mime.includes('typescript') || mime.includes('svg')) {
    return true;
  }
  const base = name.split('/').pop() ?? name;
  if (base === 'Dockerfile' || base.startsWith('.')) return true;
  const ext = base.includes('.') ? base.split('.').pop()!.toLowerCase() : '';
  return TEXT_EXT.has(ext);
}

function triggerDownload(filename: string, data: BlobPart, mime: string) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.split('/').pop() || filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function base64ToUint8(b64: string): Uint8Array {
  const cleaned = b64.replace(/\s+/g, '');
  const bin = atob(cleaned);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = String(r.result ?? '');
      const i = res.indexOf(',');
      resolve(i >= 0 ? res.slice(i + 1) : res);
    };
    r.onerror = () => reject(r.error ?? new Error('read fail'));
    r.readAsDataURL(file);
  });
}

function TreeRow({ node, depth, expanded, onToggle, activePath, onSelect, onDownload }: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  activePath: string | null;
  onSelect: (path: string) => void;
  onDownload: (path: string) => void;
}) {
  const pad = { paddingLeft: `${8 + depth * 14}px` };
  if (node.type === 'tree') {
    const open = expanded.has(node.path);
    return (
      <>
        <button
          type='button'
          onClick={() => onToggle(node.path)}
          style={pad}
          className='w-full flex items-center gap-1.5 py-1 pr-2 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 rounded-lg text-left'
          title={node.path}
        >
          {open ? <ChevronDown size={13} className='flex-shrink-0' /> : (
            <ChevronRight size={13} className='flex-shrink-0' />
          )}
          <Folder size={13} className='flex-shrink-0 text-gray-500' />
          <span className='truncate'>{node.name}</span>
        </button>
        {open && node.children?.map((child) => (
          <TreeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            activePath={activePath}
            onSelect={onSelect}
            onDownload={onDownload}
          />
        ))}
      </>
    );
  }
  const active = activePath === node.path;
  return (
    <div
      className={`group flex items-center rounded-lg ${
        active ? 'bg-blue-600/15' : 'hover:bg-gray-800/60'
      }`}
    >
      <button
        type='button'
        onClick={() => onSelect(node.path)}
        style={pad}
        className={`flex-1 min-w-0 flex items-center gap-1.5 py-1 pr-1 text-xs text-left ${
          active ? 'text-blue-300' : 'text-gray-400 group-hover:text-gray-200'
        }`}
        title={node.path}
      >
        <span className='w-[13px] flex-shrink-0' />
        <FileText size={13} className='flex-shrink-0' />
        <span className='truncate'>{node.name}</span>
      </button>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onDownload(node.path);
        }}
        title='Download'
        className='opacity-0 group-hover:opacity-100 p-1.5 mr-1 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-700/50 flex-shrink-0'
      >
        <Download size={12} />
      </button>
    </div>
  );
}

export default function CodePanel({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [activePath, setActivePath] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [themeTick, setThemeTick] = useState(0);
  const dirty = activePath != null && draft !== savedSnapshot;
  const loadPathRef = useRef<string | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const editorInstRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const activePathRef = useRef<string | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  activePathRef.current = activePath;

  // Chat + IDE: který soubor je otevřený / špinavý / kurzor
  useEffect(() => {
    const pos = editorInstRef.current?.getPosition();
    setUiContext({
      open_file: activePath,
      open_file_dirty: dirty,
      open_file_line: pos?.lineNumber ?? null,
      open_file_column: pos?.column ?? null,
      tab: 'code',
    });
  }, [activePath, dirty]);

  // ACM theme → Monaco (data-theme + solární cyklus)
  useEffect(() => {
    const apply = () => {
      if (monacoRef.current) defineAndApplyAcmTheme(monacoRef.current);
      setThemeTick((n) => n + 1);
    };
    const obs = new MutationObserver(apply);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    window.addEventListener('acm-theme-cycle', apply);
    return () => {
      obs.disconnect();
      window.removeEventListener('acm-theme-cycle', apply);
    };
  }, []);

  const toggleDir = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const treeQuery = useQuery({
    queryKey: ['codable-tree', slug],
    queryFn: () => api.mcp<TreeResp>('codable.files', { slug, action: 'tree' }),
    staleTime: 15_000,
  });

  const fileQuery = useQuery({
    queryKey: ['codable-file', slug, activePath],
    queryFn: () =>
      api.mcp<ReadResp>('codable.files', { slug, action: 'read', path: activePath }),
    enabled: !!activePath,
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!activePath || !fileQuery.isSuccess || !fileQuery.data?.ok) return;
    if (fileQuery.data.encoding === 'base64') {
      setDraft('');
      setSavedSnapshot('');
      loadPathRef.current = activePath;
      return;
    }
    const content = fileQuery.data.content ?? '';
    if (loadPathRef.current === activePath && dirty) return;
    setDraft(content);
    setSavedSnapshot(content);
    loadPathRef.current = activePath;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, fileQuery.isSuccess, fileQuery.dataUpdatedAt]);

  const selectPath = useCallback((path: string) => {
    if (path === activePath) return;
    if (dirty) {
      const ok = window.confirm(
        t(
          'codable.codeUnsavedConfirm',
          'Máš neuložené změny. Opravdu přepnout soubor? Změny se ztratí.',
        ),
      );
      if (!ok) return;
    }
    loadPathRef.current = null;
    setActivePath(path);
    setDraft('');
    setSavedSnapshot('');
  }, [activePath, dirty, t]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!activePath) throw new Error('no path');
      return api.mcp<{ ok: boolean; error?: string }>('codable.workspace_write', {
        slug,
        path: activePath,
        content: draft,
      });
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.codeSaveFail', 'Uložení selhalo'));
        return;
      }
      setSavedSnapshot(draft);
      toast.success(
        t('codable.codeSaved', 'Uloženo do DEV workspace — preview se aktualizuje'),
      );
      qc.invalidateQueries({ queryKey: ['codable-file', slug, activePath] });
      qc.invalidateQueries({ queryKey: ['codable-tree', slug] });
      window.dispatchEvent(
        new CustomEvent('codable:preview-refresh', { detail: { slug } }),
      );
    },
    onError: (e: Error) => {
      toast.error(e.message || t('codable.codeSaveFail', 'Uložení selhalo'));
    },
  });

  const downloadPath = useCallback(async (path: string) => {
    try {
      // Otevřený textový soubor — i s neuloženými změnami (draft)
      if (path === activePath && !fileQuery.data?.encoding) {
        const text = dirty ? draft : (draft || fileQuery.data?.content || '');
        if (text != null && fileQuery.data?.encoding !== 'base64') {
          triggerDownload(path, text, 'text/plain;charset=utf-8');
          toast.success(t('codable.codeDownloaded', 'Soubor stažen'));
          return;
        }
      }
      const r = await api.mcp<ReadResp>('codable.files', {
        slug,
        action: 'read',
        path,
      });
      if (!r.ok) {
        toast.error(r.error ?? t('codable.codeFileError'));
        return;
      }
      if (r.encoding === 'base64' && r.content) {
        const bytes = base64ToUint8(r.content);
        triggerDownload(
          path,
          new Blob([bytes], { type: 'application/octet-stream' }),
          'application/octet-stream',
        );
      } else {
        triggerDownload(path, r.content ?? '', 'text/plain;charset=utf-8');
      }
      toast.success(t('codable.codeDownloaded', 'Soubor stažen'));
    } catch (e) {
      toast.error((e as Error).message);
    }
  }, [activePath, dirty, draft, fileQuery.data, slug, t]);

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const baseName = file.name.replace(/\\/g, '/').split('/').pop() || 'upload.bin';
      const dir = activePath && activePath.includes('/')
        ? activePath.slice(0, activePath.lastIndexOf('/'))
        : '';
      const defaultPath = dir ? `${dir}/${baseName}` : baseName;
      const path = window.prompt(
        t(
          'codable.codeUploadPath',
          'Cesta v projektu (repo-root relative):',
        ),
        defaultPath,
      );
      if (!path || !path.trim()) throw new Error('cancelled');
      const dest = path.trim().replace(/^\.\//, '').replace(/^\/+/, '');
      const asText = isProbablyTextFile(baseName, file.type || '');
      if (asText) {
        const content = await file.text();
        return api.mcp<{ ok: boolean; error?: string; path?: string }>(
          'codable.workspace_write',
          { slug, path: dest, content },
        ).then((r) => ({ ...r, dest, asText: true as const }));
      }
      const b64 = await fileToBase64(file);
      return api.mcp<{ ok: boolean; error?: string; path?: string }>(
        'codable.workspace_write',
        { slug, path: dest, content: b64, encoding: 'base64' },
      ).then((r) => ({ ...r, dest, asText: false as const }));
    },
    onSuccess: (r) => {
      if (!r || (r as { error?: string }).error === 'cancelled') return;
      if (!r.ok) {
        toast.error(r.error ?? t('codable.codeUploadFail', 'Nahrání selhalo'));
        return;
      }
      toast.success(
        t('codable.codeUploaded', 'Nahráno do DEV workspace: {{path}}', {
          path: r.dest,
        }),
      );
      qc.invalidateQueries({ queryKey: ['codable-tree', slug] });
      if (r.asText) {
        loadPathRef.current = null;
        setActivePath(r.dest);
      }
      window.dispatchEvent(
        new CustomEvent('codable:preview-refresh', { detail: { slug } }),
      );
    },
    onError: (e: Error) => {
      if (e.message === 'cancelled') return;
      toast.error(e.message || t('codable.codeUploadFail', 'Nahrání selhalo'));
    },
  });

  const onUploadFiles = (list: FileList | null) => {
    if (!list?.length) return;
    // Po jednom — path prompt per file
    void (async () => {
      for (const f of Array.from(list)) {
        try {
          await uploadMut.mutateAsync(f);
        } catch {
          /* toast v onError */
        }
      }
      if (uploadRef.current) uploadRef.current.value = '';
    })();
  };

  const entries = treeQuery.data?.entries ?? [];
  const tree = useMemo(() => buildTree(entries), [entries]);
  const filterLower = filter.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      filterLower
        ? entries.filter((e) => e.type === 'blob' && e.path.toLowerCase().includes(filterLower))
        : [],
    [entries, filterLower],
  );

  const isBinary = fileQuery.data?.encoding === 'base64';
  const language = activePath ? langFromPath(activePath) : 'plaintext';

  const copyContent = () => {
    navigator.clipboard.writeText(draft)
      .then(() => toast.success(t('codable.codeCopied')))
      .catch(() => toast.error(t('codable.codeCopyFail')));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        if (!activePath || !dirty || isBinary || saveMut.isPending) return;
        e.preventDefault();
        saveMut.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activePath, dirty, isBinary, saveMut]);

  const handleEditorMount: OnMount = (editor, monaco) => {
    monacoRef.current = monaco;
    editorInstRef.current = editor;
    defineAndApplyAcmTheme(monaco);
    editor.onDidChangeCursorPosition((e) => {
      const path = activePathRef.current;
      if (!path) return;
      setUiContext({
        open_file: path,
        open_file_line: e.position.lineNumber,
        open_file_column: e.position.column,
      });
    });
  };

  // themeTick: re-render Editor s aktuálním theme id po změně schématu
  void themeTick;

  return (
    <div className='flex-1 min-h-0 min-w-0 flex bg-gray-950 overflow-hidden'>
      {/* Strom souborů */}
      <div className='w-64 flex-shrink-0 border-r border-gray-800 flex flex-col min-h-0'>
        <div className='p-2 border-b border-gray-800/60 space-y-2'>
          <Input
            size='sm'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('codable.codeFilterPh')}
            spellCheck={false}
          />
          <div className='flex gap-1'>
            <button
              type='button'
              onClick={() => uploadRef.current?.click()}
              disabled={uploadMut.isPending}
              className='flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-gray-300 bg-gray-800/60 border border-gray-700 hover:bg-gray-800 hover:text-white disabled:opacity-40'
              title={t('codable.codeUpload', 'Nahrát soubor do workspace')}
            >
              {uploadMut.isPending
                ? <Loader2 size={12} className='animate-spin' />
                : <Upload size={12} />}
              {t('codable.codeUpload', 'Nahrát')}
            </button>
            <input
              ref={uploadRef}
              type='file'
              className='hidden'
              multiple
              onChange={(e) => onUploadFiles(e.target.files)}
            />
          </div>
        </div>
        <div className='flex-1 overflow-y-auto p-1'>
          {treeQuery.isLoading
            ? (
              <div className='p-3 space-y-2' aria-hidden='true'>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className='h-3 rounded bg-gray-800 animate-pulse' />
                ))}
              </div>
            )
            : treeQuery.data?.ok === false
            ? (
              <div className='p-3 text-xs text-red-400'>
                {treeQuery.data.error ?? t('codable.codeTreeError')}
              </div>
            )
            : filterLower
            ? (
              filtered.length === 0
                ? (
                  <div className='p-3 text-xs text-gray-600'>
                    {t('codable.codeFilterEmpty', 'Žádný soubor neodpovídá filtru')}
                  </div>
                )
                : filtered.map((e) => (
                  <div
                    key={e.path}
                    className={`group flex items-center rounded-lg ${
                      activePath === e.path ? 'bg-blue-600/15' : 'hover:bg-gray-800/60'
                    }`}
                  >
                    <button
                      type='button'
                      onClick={() => selectPath(e.path)}
                      className={`flex-1 min-w-0 flex items-center gap-1.5 py-1 px-2 text-xs text-left ${
                        activePath === e.path
                          ? 'text-blue-300'
                          : 'text-gray-400 group-hover:text-gray-200'
                      }`}
                      title={e.path}
                    >
                      <FileText size={13} className='flex-shrink-0' />
                      <span className='truncate font-mono'>{e.path}</span>
                    </button>
                    <button
                      type='button'
                      onClick={() => downloadPath(e.path)}
                      className='opacity-0 group-hover:opacity-100 p-1.5 mr-1 rounded text-gray-500 hover:text-gray-200 flex-shrink-0'
                      title={t('codable.codeDownload', 'Stáhnout')}
                    >
                      <Download size={12} />
                    </button>
                  </div>
                ))
            )
            : tree.map((node) => (
              <TreeRow
                key={node.path}
                node={node}
                depth={0}
                expanded={expanded}
                onToggle={toggleDir}
                activePath={activePath}
                onSelect={selectPath}
                onDownload={downloadPath}
              />
            ))}
        </div>
      </div>

      {/* Editor */}
      <div className='flex-1 min-w-0 flex flex-col min-h-0'>
        {!activePath
          ? (
            <div className='flex-1 flex items-center justify-center'>
              <EmptyState
                icon={<FileText size={24} />}
                title={t('codable.codePickFile')}
                description={t(
                  'codable.codeEditorHint',
                  'Vyber soubor — editor se zvýrazněním syntaxe, Ctrl+S uloží do DEV workspace.',
                )}
              />
            </div>
          )
          : (
            <>
              <div className='flex items-center gap-2 px-3 py-1.5 border-b border-gray-800 bg-gray-950/80'>
                <span className='text-xs font-mono text-gray-300 truncate flex-1 min-w-0'>
                  {activePath}
                  {dirty && (
                    <span
                      className='ml-1.5 text-amber-400'
                      title={t('codable.codeDirty', 'Neuložené změny')}
                    >
                      •
                    </span>
                  )}
                </span>
                {typeof fileQuery.data?.size === 'number' && !dirty && (
                  <span className='text-[11px] text-gray-600 flex-shrink-0'>
                    {formatSize(fileQuery.data.size)}
                  </span>
                )}
                {dirty && (
                  <span className='text-[10px] text-amber-400/90 flex-shrink-0'>
                    {t('codable.codeDirty', 'Neuloženo')}
                  </span>
                )}
                <button
                  type='button'
                  onClick={copyContent}
                  disabled={!draft && !isBinary}
                  title={t('codable.codeCopy')}
                  className='p-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-gray-800/60 disabled:opacity-40 flex-shrink-0'
                >
                  <Copy size={13} />
                </button>
                <button
                  type='button'
                  onClick={() => activePath && downloadPath(activePath)}
                  title={t('codable.codeDownload', 'Stáhnout soubor')}
                  className='p-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-gray-800/60 flex-shrink-0'
                >
                  <Download size={13} />
                </button>
                <button
                  type='button'
                  onClick={() => saveMut.mutate()}
                  disabled={!dirty || isBinary || saveMut.isPending || fileQuery.isLoading}
                  title={t('codable.codeSave', 'Uložit (Ctrl+S)')}
                  className='inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0 disabled:opacity-40 bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 disabled:hover:bg-blue-600/20'
                >
                  {saveMut.isPending
                    ? <Loader2 size={12} className='animate-spin' />
                    : <Save size={12} />}
                  {t('codable.codeSave', 'Uložit')}
                </button>
              </div>
              <div className='flex-1 min-h-0'>
                {fileQuery.isLoading && loadPathRef.current !== activePath
                  ? (
                    <div className='p-4 space-y-2' aria-hidden='true'>
                      <Loader2 size={16} className='animate-spin text-gray-600 mb-2' />
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className='h-3 rounded bg-gray-800 animate-pulse'
                          style={{ width: `${90 - (i % 4) * 15}%` }}
                        />
                      ))}
                    </div>
                  )
                  : fileQuery.data?.ok === false
                  ? (
                    <div className='p-4 text-xs text-red-400'>
                      {fileQuery.data.error ?? t('codable.codeFileError')}
                    </div>
                  )
                  : isBinary
                  ? (
                    <div className='p-4 text-xs text-gray-400 space-y-3'>
                      <p>
                        {t(
                          'codable.codeBinary',
                          'Binární soubor — v editoru se needituje. Můžeš ho stáhnout nebo nahrát novou verzi.',
                        )}
                      </p>
                      <div className='flex gap-2'>
                        <button
                          type='button'
                          onClick={() => activePath && downloadPath(activePath)}
                          className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700'
                        >
                          <Download size={12} />
                          {t('codable.codeDownload', 'Stáhnout')}
                        </button>
                        <button
                          type='button'
                          onClick={() => uploadRef.current?.click()}
                          className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700'
                        >
                          <Upload size={12} />
                          {t('codable.codeUpload', 'Nahrát')}
                        </button>
                      </div>
                      {fileQuery.data?.note && (
                        <p className='text-gray-600 font-mono'>{fileQuery.data.note}</p>
                      )}
                    </div>
                  )
                  : (
                    <Editor
                      key={`${MONACO_THEME_ID}-${themeTick}`}
                      height='100%'
                      language={language}
                      theme={MONACO_THEME_ID}
                      value={draft}
                      path={activePath}
                      onChange={(v) => setDraft(v ?? '')}
                      onMount={handleEditorMount}
                      beforeMount={(monaco) => {
                        monacoRef.current = monaco;
                        defineAndApplyAcmTheme(monaco);
                      }}
                      options={{
                        fontSize: 13,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        renderLineHighlight: 'line',
                        padding: { top: 8, bottom: 8 },
                        bracketPairColorization: { enabled: true },
                      }}
                    />
                  )}
              </div>
            </>
          )}
      </div>
    </div>
  );
}
